import React, { Fragment, useState } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, } from 'reactstrap';
import { Link } from 'react-router-dom';
import { isEmpty, groupBy } from 'lodash';
import classnames from 'classnames';
import { format as formatDate } from 'date-fns';
import { toast } from 'react-toastify';

import { malls, mallOrderSkipReasons, } from '../../shared/config';
import { generalFields, mailFields } from '../../shared/models/shop';
import { functions } from '../../firebase';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import Page from '../hocs/Page';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ProgressButton from '../ProgressButton';
import SettingsTabs from '../SettingsTabs';
import env from '../../env';

const { entries } = Object;

const authenticateShopMailDomain = functions.httpsCallable('authenticateShopMailDomain', { timeout: 60000 });
const validateShopMailDomain = functions.httpsCallable('validateShopMailDomain', { timeout: 60000 });
const fetchShopifyLocations = functions.httpsCallable('fetchShopifyLocations', { timeout: 60000 });

// Shopify の在庫拠点（出荷元ロケーション）を画面から選べるようにする。
// インストール時の自動設定が失敗しても、ここから選び直せば出荷依頼の作成を再開できる。
// これが無いと「ロケーションが未設定」という表示だけが残り、倉庫の棚を管理する
// 別画面（/settings/otherLocations）に迷い込む原因になる。
function ShopifyLocationPicker ({ shopRef, shop }) {
  const [locations, setLocations] = useState(null);

  const onClickFetch = async () => {
    try {
      const { data } = await fetchShopifyLocations({ tenantId: shopRef.parent.parent.id, shopId: shopRef.id });
      setLocations(data);
      if (isEmpty(data)) toast.warn('Shopify側に有効なロケーションが見つかりませんでした');
    } catch(e) {
      console.error(e);
      toast.error(`ロケーションの取得に失敗しました。${e.message}`);
    }
  };
  const onClickSelect = async (location) => {
    try {
      await shopRef.update({ shopifyLocationId: location.id });
      toast.success(`出荷元ロケーションを「${location.name}」に設定しました`);
    } catch(e) {
      console.error(e);
      toast.error(`設定に失敗しました。${e.message}`);
    }
  };

  const selectedLocation = (locations || []).find(_ => _.id === shop.shopifyLocationId);

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-2">
        <ProgressButton color="primary" process={onClickFetch} disabled={!shop.shopifyAccessToken}>
          出荷元ロケーションを選択
        </ProgressButton>
        {
          shop.shopifyLocationId && (
            <span className="text-muted small ml-2">
              設定済み{selectedLocation && `: ${selectedLocation.name}`}
            </span>
          )
        }
      </div>
      {
        locations != null && locations.length > 0 && (
          <table className="table table-sm table-bordered small mb-0">
            <thead className="thead-light">
              <tr>
                <th>Shopifyのロケーション名</th>
                <th style={{ width: 140 }}>オンライン注文</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {
                locations.map((location) => {
                  const isSelected = location.id === shop.shopifyLocationId;
                  return (
                    <tr key={location.id}>
                      <td>{location.name}</td>
                      <td>{location.fulfillsOnlineOrders ? '対応' : '—'}</td>
                      <td>
                        {
                          isSelected ? (
                            <span className="text-success">
                              <span className="fas fa-check mr-1" />
                              選択中
                            </span>
                          ) : (
                            <ProgressButton size="sm" color="secondary" process={_ => onClickSelect(location)}>
                              これにする
                            </ProgressButton>
                          )
                        }
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        )
      }
    </div>
  );
}

function ShopMailDomainAuthPanel ({ tenantId, shopId, shop }) {
  const email = shop.yahooMailFromAddress;
  const auth = shop.sendgridDomainAuth;
  const emailDomain = (email || '').split('@')[1];
  const isDomainMismatch = auth?.domain && emailDomain && auth.domain !== emailDomain;

  const onClickAuthenticate = async () => {
    if(!email) { toast.error('先に「Yahooメール 送信者アドレス」を設定してください'); return; }
    if(!window.confirm(`${emailDomain} のドメイン認証をSendGridに登録します。よろしいですか？`)) return;
    try {
      await authenticateShopMailDomain({ tenantId, shopId, email });
      toast.success('認証情報を生成しました。以下のCNAMEをDNSに追加してください');
    } catch(e) {
      console.error(e);
      toast.error(`失敗しました。${e.message}`);
    }
  };
  const onClickValidate = async () => {
    try {
      const { data } = await validateShopMailDomain({ tenantId, shopId });
      if(data.valid) toast.success('ドメイン認証が完了しました');
      else toast.warn('まだDNSが反映されていないようです。数分待って再度お試しください');
    } catch(e) {
      console.error(e);
      toast.error(`失敗しました。${e.message}`);
    }
  };

  return (
    <div className="mb-4" style={{ maxWidth: 720 }}>
      <h5 className="mb-3">送信者アドレス ドメイン認証</h5>
      <div className="mb-3">
        <div><strong>送信者アドレス:</strong> {email || <span className="text-danger">未設定</span>}</div>
        {auth?.domain && <div><strong>登録ドメイン:</strong> {auth.domain}</div>}
        <div>
          <strong>認証状態:</strong>{' '}
          {auth?.valid ? <span className="text-success">認証済</span> : <span className="text-warning">未認証</span>}
        </div>
        {isDomainMismatch && <div className="text-danger small mt-1">※送信者アドレスのドメインが変更されています。再度「認証を開始」してください</div>}
      </div>
      <div className="mb-3">
        <ProgressButton className="mr-2" color="primary" process={onClickAuthenticate} disabled={!email}>
          {!auth?.sendgridId ? '認証を開始' : isDomainMismatch ? '別ドメインで認証をやり直す' : '認証情報を再取得'}
        </ProgressButton>
        {auth?.sendgridId && !isDomainMismatch && (
          <ProgressButton color="success" process={onClickValidate}>
            DNSを検証
          </ProgressButton>
        )}
      </div>
      {
        (auth?.cnameRecords || []).length > 0 && (
          <div>
            <div className="mb-1 small">以下のCNAMEレコードをドメイン({auth.domain})のDNSに登録してください:</div>
            <table className="table table-sm table-bordered small">
              <thead className="thead-light">
                <tr>
                  <th>種別</th>
                  <th>Host</th>
                  <th>Value</th>
                  <th>状態</th>
                </tr>
              </thead>
              <tbody>
                {
                  auth.cnameRecords.map((r, i) => (
                    <tr key={i}>
                      <td>CNAME</td>
                      <td style={{ wordBreak: 'break-all' }}>{r.host}</td>
                      <td style={{ wordBreak: 'break-all' }}>{r.data}</td>
                      <td>{r.valid ? <span className="text-success">OK</span> : <span className="text-muted">未反映</span>}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

const FieldList = ({ fields, shop }) => {
  const visibleEntries = entries(fields).filter(([, field]) => !(field.hidden && field.hidden(shop)));

  return (
    <div style={{ maxWidth: 720 }}>
      {
        visibleEntries.map(([name, field]) => {
          const value = shop[name];
          const displayValue = field.type === 'password'
            ? (isEmpty(value) ? '' : '********')
            : field.type === 'boolean'
            ? (value !== false ? 'ON' : 'OFF')
            // Firestoreのタイムスタンプはそのまま描画できないため文字列にする。
            : field.type === 'date'
            ? (value?.toDate ? formatDate(value.toDate(), 'yyyy/MM/dd') : '')
            : (field.type === 'select' ? (field.options.find(_ => _.value === value)?.label ?? value) : value);
          const isMultiline = field.type === 'text';

          return (
            <div key={name} className="mb-3">
              <div className="small text-muted mb-1">{field.label}</div>
              {
                isMultiline ? (
                  <div className="border rounded bg-light px-3 py-2 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {isEmpty(displayValue) ? <span className="text-muted">（未設定）</span> : displayValue}
                  </div>
                ) : (
                  <div style={{ wordBreak: 'break-word' }}>
                    {isEmpty(String(displayValue ?? '')) ? <span className="text-muted">（未設定）</span> : displayValue}
                  </div>
                )
              }
            </div>
          );
        })
      }
    </div>
  );
};

// 定期取込（10分おき）を待たずに取り込む。待たせると、注文を作っても何も起きないように見えて
// 「同期されない」と判断される。同期で実行し、取り込まれなかった注文はその理由をトーストで出す。
const pullShopifyOrdersNow = functions.httpsCallable('pullShopifyOrdersNow', { timeout: 550000 });

const pullErrorMessages = {
  noSender: '配送元（送り主）が未設定のため取り込めません。',
  noStartOrderNumber: '連携開始注文番号が未設定のため取り込めません。',
};

function PullShopifyOrdersButton ({ shop }) {
  const onClick = async () => {
    try {
      const { data: { error, fetchedCount, beforeStartOrderCount, unpaidOrderNames, outcomes } } = await pullShopifyOrdersNow({ tenantId: shop.ref.parent.parent.id, shopId: shop.id });
      if (error) {
        toast.error(pullErrorMessages[error] || (error.match(/token refresh failed|401/) ? 'Shopifyとの連携が切れています。アプリを再インストールしてください。' : `取り込みに失敗しました。${error}`), { autoClose: false });
        return;
      }
      const imported = outcomes.filter(_ => _.shippingRequestId);
      const outcomesGroupedBySkipReason = groupBy(outcomes.filter(_ => _.skipReason), 'skipReason');
      imported.length > 0 && toast.success(`${imported.length}件の注文を取り込み、出荷依頼に反映しました（${imported.map(_ => _.name).join(', ')}）`);
      entries(outcomesGroupedBySkipReason).forEach(([skipReason, _]) => {
        toast.warn(`出荷依頼を作成しませんでした（${mallOrderSkipReasons[skipReason] || skipReason}）：${_.map(_ => _.name).join(', ')}`, { autoClose: false });
      });
      unpaidOrderNames.length > 0 && toast.info(`支払いが完了していないため取り込んでいません：${unpaidOrderNames.join(', ')}`, { autoClose: false });
      outcomes.length === 0 && unpaidOrderNames.length === 0 && toast.info(
        `取り込み対象の注文はありませんでした。注文番号 ${shop.shopifyStartOrderNumber} 以降の、支払い済みで未発送の注文が対象です。` +
        (beforeStartOrderCount > 0 ? `（Shopifyの直近の未発送注文${fetchedCount}件のうち${beforeStartOrderCount}件は、連携開始注文番号より前のため対象外です）` : ''),
        { autoClose: false },
      );
    } catch(e) {
      console.error(e);
      toast.error(`取り込みに失敗しました。${e.message}`, { autoClose: false });
    }
  };

  return (
    <div className="d-flex align-items-center mt-2">
      <ProgressButton size="sm" color="primary" process={onClick}>
        今すぐ受注を取り込む
      </ProgressButton>
      <Link to="/mallOrders" className="ml-3 small">受注一覧を開く</Link>
    </div>
  );
}

// Shopify 店舗の受注連携の準備状況を表示する。
// 必要な設定が欠けていると受注が 1 件も取り込まれず、原因が画面から分からないため
// （App Store 要件 2.1.4「データの正確な同期」）、不足項目と対処を明示する。
//
// blockers（受注そのものが取り込まれない）と warnings（受注は取り込まれるが出荷依頼が
// 作られない）を必ず区別する。pullShopifyOrders は shopifyLocationId 未設定でも受注を
// 取り込む（ロケーション判定は出荷依頼の作成段階でのみ行う）ので、これを「取り込まれません」
// と表示してはいけない。実害の無い状態を致命的エラーのように見せると、解消しようとして
// 無関係な画面（倉庫の棚を管理する「ロケーション」設定）に迷い込む原因になる。
function ShopifyOrderSyncStatusPanel ({ shop }) {
  const { shopifyShop, shopifyAccessToken, shopifyLocationId, shopifyStartOrderNumber, isOrderSyncEnabled, shopifyReauthRequiredAt } = shop;

  // 受注の取り込み自体が止まる要因。pullShopifyOrders の早期 return 条件と対応させる。
  const blockers = [
    // 再認可が必要な状態を、単なる「未インストール」より先に案内する。
    // Shopify 側は「インストール済み」に見えるので、何をすればいいか明示しないと詰む。
    shopifyReauthRequiredAt && {
      label: 'Shopifyとの連携が切れています。再インストールが必要です',
      hint: 'Shopify管理画面でこのアプリを一度アンインストールし、上の「Shopifyアプリをインストール」から入れ直してください。連携が切れている間、注文は取り込まれません。',
    },
    !shopifyAccessToken && !shopifyReauthRequiredAt && {
      label: 'Shopifyアプリが未インストールです',
      hint: '上の「Shopifyアプリをインストール」から連携してください。インストール時に必要な設定は自動で入ります。',
    },
    isEmpty(shopifyShop) && {
      label: 'Shopify Shop ドメインが未設定です',
      hint: 'xxx.myshopify.com の形式で入力してください（ストアの表示名ではありません）。',
    },
    shopifyAccessToken && isEmpty(shopifyStartOrderNumber) && {
      label: '連携開始注文番号が未設定です',
      hint: 'この番号以降の注文のみ取り込みます。未設定の間は受注を取り込みません。',
    },
    isOrderSyncEnabled === false && {
      label: '受注連携が無効になっています',
      hint: '「受注連携を有効にする」をONにしてください。',
    },
  ].filter(Boolean);

  // 受注は取り込まれるが、出荷までは自動で進まない要因。
  const warnings = [
    shopifyAccessToken && isEmpty(shopifyLocationId) && {
      label: '出荷元ロケーションが未設定のため、出荷依頼は自動作成されません',
      hint: 'Shopifyの注文は受注一覧に取り込まれます。LOGISPから出荷まで行うには、この店舗の出荷元ロケーション（Shopify側の在庫拠点）を下の「出荷元ロケーションを選択」から選んでください。',
    },
  ].filter(Boolean);

  if (blockers.length === 0 && warnings.length === 0) {
    return (
      <div className="alert alert-success">
        <span className="fas fa-check-circle mr-2" />
        受注連携の設定は完了しています。この店舗の注文は自動で取り込まれます。
        <div className="small mt-1">
          注文番号 {shopifyStartOrderNumber} 以降の支払い済みの注文が受注一覧に並びます。出荷依頼が作成されるのは日本国内宛（郵便番号 000-0000 形式）の注文のみで、作成されなかった注文は受注一覧に理由を表示します。
        </div>
        <PullShopifyOrdersButton shop={shop} />
      </div>
    );
  }

  return (
    <Fragment>
      {
        blockers.length > 0 && (
          <div className="alert alert-warning">
            <div className="mb-2">
              <span className="fas fa-exclamation-triangle mr-2" />
              受注連携が未完了です。以下を解消するまで注文は取り込まれません。
            </div>
            <ul className="mb-0 pl-4">
              {blockers.map(_ => (
                <li key={_.label}>
                  {_.label}
                  <div className="small text-muted">{_.hint}</div>
                </li>
              ))}
            </ul>
          </div>
        )
      }
      {
        warnings.length > 0 && (
          <div className="alert alert-info">
            <div className="mb-2">
              <span className="fas fa-info-circle mr-2" />
              受注の取り込みは有効です。出荷まで自動化するには、以下の設定が必要です。
            </div>
            <ul className="mb-0 pl-4">
              {warnings.map(_ => (
                <li key={_.label}>
                  {_.label}
                  <div className="small text-muted">{_.hint}</div>
                </li>
              ))}
            </ul>
            {blockers.length === 0 && <PullShopifyOrdersButton shop={shop} />}
          </div>
        )
      }
    </Fragment>
  );
}

export default Page(function SettingsShop (props) {
  const { tenant, match: { params: { shopId } } } = props;
  const shopsRef = tenant.ref.collection('shops');
  const shopRef = shopsRef.doc(shopId);
  const shop = useDocumentSubscription(shopRef, [shopId]);
  const senders = useCollectionSubscription(tenant.ref.collection('senders'), [tenant.id]);
  const [activeTab, setActiveTab] = useState('general');

  if (shop == null) return null;

  const { mall, name, yahoo, tts, shopifyAuthorizedAt, shopifyAccessToken } = shop;
  const authorizedAt = yahoo?.authorizedAt;
  const ttsAuthorizedAt = tts?.authorizedAt;
  const hasMailTab = mall === 'rakuten' || mall === 'yahoo';

  return (
    <div className="settings-shop container-fluid">
      <div className="p-4 bg-white my-4">
        <SettingsTabs activeTab="shops" />
        <div className="d-flex align-items-center mb-3">
          <Link to="/settings/shops" className="btn btn-link pl-0">
            <span className="fas fa-chevron-left mr-1" />
            店舗一覧へ戻る
          </Link>
        </div>
        <div className="d-flex align-items-center mb-3">
          <span className="badge badge-secondary mr-2">{malls[mall]?.label}</span>
          <h4 className="mb-0">{name}</h4>
        </div>

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink className={classnames('cursor-pointer', { active: activeTab === 'general' })} onClick={_ => setActiveTab('general')}>
              一般
            </NavLink>
          </NavItem>
          {
            hasMailTab && (
              <NavItem>
                <NavLink className={classnames('cursor-pointer', { active: activeTab === 'mail' })} onClick={_ => setActiveTab('mail')}>
                  メール
                </NavLink>
              </NavItem>
            )
          }
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="general">
            <div className="d-flex justify-content-start align-items-center mb-3">
              {
                mall === 'yahoo' && (
                  <Fragment>
                    <a href={`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authYahoo?tenantId=${tenant.id}&shopId=${shopId}`} className="btn btn-secondary mr-2" target="_blank" rel="noopener noreferrer">
                      Yahoo認証
                      <span className="fas fa-external-link-alt ml-1" />
                    </a>
                    {
                      authorizedAt && (
                        <span className="text-muted small mr-2">{formatDate(authorizedAt.toDate(), 'yyyy/MM/dd HH:mm')}認証</span>
                      )
                    }
                  </Fragment>
                )
              }
              {
                mall === 'tts' && (
                  <Fragment>
                    <a href={`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authTts?tenantId=${tenant.id}&shopId=${shopId}`} className="btn btn-secondary mr-2" target="_blank" rel="noopener noreferrer">
                      TikTok Shop認証
                      <span className="fas fa-external-link-alt ml-1" />
                    </a>
                    {
                      ttsAuthorizedAt && (
                        <span className="text-muted small mr-2">{formatDate(ttsAuthorizedAt.toDate(), 'yyyy/MM/dd HH:mm')}認証</span>
                      )
                    }
                  </Fragment>
                )
              }
              {
                mall === 'shopify' && (
                  <Fragment>
                    {
                      // 既存カスタムアプリ連携済み店舗（shopifyAccessToken 保持）には出さない。
                      // 誤クリックで OAuth が走り既存連携を上書きするのを防ぐ。
                      // 未連携店舗（新規／審査用テスト店舗）にのみインストール導線を出す。
                      !shopifyAccessToken && (
                        <a href={`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authShopify?tenantId=${tenant.id}&shopId=${shopId}`} className="btn btn-secondary mr-2" target="_blank" rel="noopener noreferrer">
                          Shopifyアプリをインストール
                          <span className="fas fa-external-link-alt ml-1" />
                        </a>
                      )
                    }
                    {
                      shopifyAuthorizedAt && (
                        <span className="text-muted small mr-2">{formatDate(shopifyAuthorizedAt.toDate(), 'yyyy/MM/dd HH:mm')}インストール</span>
                      )
                    }
                  </Fragment>
                )
              }
              <EditButton itemRef={shopRef} FormModal={ModelFormModal} formProps={{ title: '店舗 編集（一般）', fields: generalFields({ tenant, senders, mall }), }} />
              <DeleteButton item={shop} itemRef={shopRef} className="ml-2" onFinish={_ => props.history.push('/settings/shops')} />
            </div>
            {mall === 'shopify' && <ShopifyOrderSyncStatusPanel shop={shop} />}
            {mall === 'shopify' && shopifyAccessToken && <ShopifyLocationPicker shopRef={shopRef} shop={shop} />}
            <FieldList fields={generalFields({ tenant, senders, mall })} shop={shop} />
          </TabPane>
          {
            hasMailTab && (
              <TabPane tabId="mail">
                <div className="d-flex justify-content-start mb-3">
                  <EditButton itemRef={shopRef} FormModal={ModelFormModal} formProps={{ title: '店舗 編集（メール）', fields: mailFields({ tenant, senders, mall }), }} />
                </div>
                <FieldList fields={mailFields({ tenant, senders, mall })} shop={shop} />
                {
                  mall === 'yahoo' && (
                    <ShopMailDomainAuthPanel tenantId={tenant.id} shopId={shopId} shop={shop} />
                  )
                }
              </TabPane>
            )
          }
        </TabContent>
      </div>
    </div>
  );
});
